import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSetRecoilState } from 'recoil';

import { TeamTaskUpdateReq, teamApi } from '../api';
import {
  AssignedTeamTask,
  AssignedTeamTasksRangeResult,
  TEAM_ASSIGNED_TASKS_QUERY_KEY,
  TEAM_TASKS_QUERY_KEY,
} from '../queries';
import { eventsSnackBarAtom } from '../recoil';
import { translateI18n } from '../../i18n';

interface AssignedTeamTaskMoveParam {
  updatedTask: AssignedTeamTask;
  newDate: string;
}

interface UpdateTeamTaskParams {
  teamId: string;
  boardId: string;
  taskId: string;
  body: TeamTaskUpdateReq;
  assignedTeamTaskMoveParam?: AssignedTeamTaskMoveParam;
  showSnackbar?: boolean;
}

export const useUpdateTeamTask = () => {
  const queryClient = useQueryClient();
  const setEventsSnackbar = useSetRecoilState(eventsSnackBarAtom);

  const updateTeamTaskMutation = useMutation({
    mutationFn: ({ teamId, boardId, taskId, body }: UpdateTeamTaskParams) => {
      return teamApi.updateTeamTask(teamId, boardId, taskId, body).then(({ data }) => data);
    },
    onMutate: async ({ teamId, boardId, taskId, assignedTeamTaskMoveParam }) => {
      if (!assignedTeamTaskMoveParam) {
        return;
      }

      await queryClient.cancelQueries({ queryKey: TEAM_ASSIGNED_TASKS_QUERY_KEY, type: 'active' });
      const previousAssignedTeamTasks = queryClient.getQueriesData<AssignedTeamTasksRangeResult>({
        queryKey: TEAM_ASSIGNED_TASKS_QUERY_KEY,
        type: 'active',
      });

      queryClient.setQueriesData<AssignedTeamTasksRangeResult>(
        { queryKey: TEAM_ASSIGNED_TASKS_QUERY_KEY, type: 'active' },
        (prev) => {
          if (!prev) {
            return prev;
          }

          const isSameTask = (candidate: AssignedTeamTask) =>
            candidate.taskId === taskId &&
            candidate.assignedTeamTask.team.teamId === teamId &&
            candidate.assignedTeamTask.project.boardId === boardId;

          let didPlaceTask = false;
          const tasksByDate = prev.tasksByDate.map((tasksByDate) => {
            const isTargetDate = tasksByDate.date === assignedTeamTaskMoveParam.newDate;

            if (isTargetDate) {
              didPlaceTask = true;
            }

            return {
              ...tasksByDate,
              tasks: isTargetDate
                ? [
                    ...tasksByDate.tasks.filter((candidate) => !isSameTask(candidate)),
                    assignedTeamTaskMoveParam.updatedTask,
                  ]
                : tasksByDate.tasks.filter((candidate) => !isSameTask(candidate)),
              updatedAt: new Date().toISOString(),
            };
          });

          if (!didPlaceTask) {
            tasksByDate.push({
              date: assignedTeamTaskMoveParam.newDate,
              tasks: [assignedTeamTaskMoveParam.updatedTask],
              updatedAt: new Date().toISOString(),
            });
          }

          return {
            ...prev,
            overdueTasks: prev.overdueTasks.filter((candidate) => !isSameTask(candidate)),
            tasksByDate,
          };
        }
      );

      return { previousAssignedTeamTasks };
    },
    onError: (_error, variables, context) => {
      context?.previousAssignedTeamTasks?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });

      if (variables.showSnackbar !== false) {
        setEventsSnackbar({ message: translateI18n('team.tasks.snackbar.updateFailed'), type: 'error', icon: 'task' });
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: TEAM_TASKS_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: TEAM_ASSIGNED_TASKS_QUERY_KEY,
        type: variables.assignedTeamTaskMoveParam ? 'active' : 'all',
      });
      queryClient.invalidateQueries({ queryKey: ['lifestack.todo.tasks'] });
      queryClient.invalidateQueries({ queryKey: ['lifestack.todo.overdueTasks'] });

      if (variables.showSnackbar !== false) {
        setEventsSnackbar({ message: translateI18n('team.tasks.snackbar.updated'), type: 'success', icon: 'task' });
      }
    },
  });

  return {
    updateTeamTask: updateTeamTaskMutation.mutateAsync,
    isUpdatingTeamTask: updateTeamTaskMutation.isPending,
    ...updateTeamTaskMutation,
  };
};
